'use client'

import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'

import {
  getItemEscrowFees,
  transformItemEscrowFeesResponse,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'

import {
  createBundleSummary,
  transformBundleSummaryDto,
} from '@marketplace-web/bundles/items-bundle-data'

type Props = {
  selectedItemIds: Array<number>
  sellerId: number
  isEnabled: boolean
  isItemAdded: boolean
}

const useBundleSummary = ({ selectedItemIds, sellerId, isEnabled, isItemAdded }: Props) => {
  const [isBundleSummaryLoading, setIsBundleSummaryLoading] = useState<boolean>(false)

  const {
    fetch: fetchSummary,
    transformedData: bundleSummary,
    isLoading: bundleLoading,
    error: bundleError,
  } = useFetch(createBundleSummary, response => transformBundleSummaryDto(response.bundle_summary))

  const {
    transformedData: itemEscrowFees,
    fetch: fetchItemEscrowFees,
    isLoading: isItemEscrowFeesLoading,
  } = useFetch(getItemEscrowFees, transformItemEscrowFeesResponse)

  const anyItemId = selectedItemIds[0]

  const fetchDebouncedSummary = useDebounce(
    async () => {
      await fetchSummary({ itemIds: selectedItemIds, sellerId, isItemAdded })
    },
    500,
    false,
  )

  const startFetchSummary = useCallback(() => {
    setIsBundleSummaryLoading(true)
    fetchDebouncedSummary()
  }, [fetchDebouncedSummary])

  useEffect(() => {
    if (!isEnabled) return

    if (anyItemId) fetchItemEscrowFees({ itemId: anyItemId })

    startFetchSummary()
  }, [isEnabled, selectedItemIds.length, startFetchSummary, fetchItemEscrowFees, anyItemId])

  useEffect(() => {
    if ((!isItemEscrowFeesLoading && !bundleLoading) || bundleError) {
      setIsBundleSummaryLoading(false)
    }
  }, [bundleError, bundleLoading, isItemEscrowFeesLoading])

  return {
    bundleSummary,
    isBundleSummaryLoading,
    escrowNoteTranslationKey: itemEscrowFees?.noteTranslationKey,
    bundleError,
  }
}

export default useBundleSummary
